import React, { useRef, useState } from 'react';
import { Spinner } from 'reactstrap';
import { ReactComponent as Del } from '../../assets/img/delIcon.svg';
import { getMediaUrl } from '../../config';
import api from 'api';

// Client-side variant editor. Mirrors the server validation rules:
//   - at least 1 variant required when present
//   - name: required, trimmed, <= 32 chars, unique case-insensitive
//   - stock: integer >= 0
//   - sku: optional, trimmed, <= 64 chars
//   - image: optional, trimmed, <= 2048 chars
//
// Props:
//   variants: Array<{ _id?, name, stock, sku?, image? }>
//   onChange: (next) => void
//   error:    string | null    aggregate error from parent
//
// The parent owns state. This component is fully controlled.

const NAME_MAX = 32;
const SKU_MAX = 64;
const IMAGE_MAX = 2048;

const inputStyle = {
    backgroundColor: '#F3F3F3',
    border: '1px solid transparent',
    borderRadius: '8px',
    fontFamily: 'Outfit',
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
};

const inputErrorStyle = {
    ...inputStyle,
    border: '1px solid #E63946',
    backgroundColor: '#FFF5F5',
};

const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
};

// Per-row validation. Returns an object of field -> message.
const validateRow = (row, idx, allRows) => {
    const errs = {};
    const name = (row.name || '').trim();
    if (!name) {
        errs.name = 'Required';
    } else if (name.length > NAME_MAX) {
        errs.name = `Max ${NAME_MAX} chars`;
    } else {
        const lower = name.toLowerCase();
        const dup = allRows.some(
            (r, i) => i !== idx && (r.name || '').trim().toLowerCase() === lower
        );
        if (dup) errs.name = 'Duplicate';
    }

    const stockNum = Number(row.stock);
    if (
        row.stock === '' ||
        row.stock === null ||
        row.stock === undefined ||
        Number.isNaN(stockNum) ||
        !Number.isInteger(stockNum) ||
        stockNum < 0
    ) {
        errs.stock = 'Invalid';
    }

    const sku = (row.sku || '').trim();
    if (sku && sku.length > SKU_MAX) {
        errs.sku = `Max ${SKU_MAX} chars`;
    }

    const image = (row.image || '').trim();
    if (image && image.length > IMAGE_MAX) {
        errs.image = `Max ${IMAGE_MAX} chars`;
    }
    return errs;
};

// Inline chevron icons — match the size used in custom.css `.vsr-reorder-btn`.
const ChevronUp = () => (
    <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <polyline points='18 15 12 9 6 15' />
    </svg>
);

const ChevronDown = () => (
    <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <polyline points='6 9 12 15 18 9' />
    </svg>
);

// Resolve a stored image string to a renderable URL.
// Absolute URLs pass through; relative paths get prefixed via getMediaUrl().
const resolveImage = (raw) => {
    const v = (raw || '').trim();
    if (!v) return '';
    return getMediaUrl(v);
};

const VariantEditor = ({ variants = [], onChange, error }) => {
    // Per-row upload UI state. Keyed by stable row key (mirrors the React key used
    // when rendering rows) so reorders/removals don't cross-pollinate state.
    const [uploadState, setUploadState] = useState({}); // { [key]: { uploading, error } }
    const [hoverPhotoKey, setHoverPhotoKey] = useState(null);
    const fileInputs = useRef({}); // { [key]: HTMLInputElement }

    const rowKey = (row, idx) => row._id || `row-${idx}`;

    const handleField = (idx, field, value) => {
        const next = variants.map((row, i) => (i === idx ? { ...row, [field]: value } : row));
        onChange(next);
    };

    const setRowUpload = (key, patch) => {
        setUploadState((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
    };

    const handlePickPhoto = (key) => {
        const el = fileInputs.current[key];
        if (el) el.click();
    };

    const handleFilePicked = async (idx, key, evt) => {
        const file = evt.target.files && evt.target.files[0];
        // Reset the input so picking the same file again still triggers onChange.
        evt.target.value = '';
        if (!file) return;
        setRowUpload(key, { uploading: true, error: null });
        try {
            const fd = new FormData();
            fd.append('image', file);
            const res = await api('post', '/brandproducts/upload-image', fd);
            const url = res && res.url;
            if (!url) throw new Error('Upload returned no URL');
            // Re-resolve idx by key in case rows were reordered mid-upload — fall
            // back to the original idx if the row is gone.
            const currentIdx = variants.findIndex((r, i) => rowKey(r, i) === key);
            const targetIdx = currentIdx >= 0 ? currentIdx : idx;
            const next = variants.map((row, i) => (i === targetIdx ? { ...row, image: url } : row));
            onChange(next);
            setRowUpload(key, { uploading: false, error: null });
        } catch (err) {
            setRowUpload(key, { uploading: false, error: 'Upload failed — try again' });
        }
    };

    const handleRemovePhoto = (idx) => {
        const next = variants.map((row, i) => (i === idx ? { ...row, image: '' } : row));
        onChange(next);
    };

    const handleAdd = () => {
        onChange([...variants, { name: '', stock: 0, sku: '', image: '' }]);
    };

    const handleRemove = (idx) => {
        onChange(variants.filter((_, i) => i !== idx));
    };

    const handleMove = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= variants.length) return;
        const next = variants.slice();
        const tmp = next[idx];
        next[idx] = next[target];
        next[target] = tmp;
        onChange(next);
    };

    return (
        <div style={{ marginTop: '6px' }}>
            <label
                style={{
                    fontWeight: 600,
                    fontSize: '16px',
                    fontFamily: 'Outfit',
                    color: '#1C1C1C',
                    display: 'block',
                    marginBottom: '6px',
                }}
            >
                Variants
            </label>

            <div
                style={{
                    display: 'flex',
                    gap: '8px',
                    fontSize: '12px',
                    color: '#828282',
                    marginBottom: '4px',
                    paddingLeft: '2px',
                }}
            >
                <div style={{ flex: '0 0 32px', textAlign: 'center' }} title='Display order'>Order</div>
                <div style={{ flex: '0 0 50px', textAlign: 'center' }} title='Click to upload or replace'>Photo</div>
                <div style={{ flex: '1 1 36%' }}>Variant name</div>
                <div style={{ flex: '0 0 22%' }}>Stock</div>
                <div style={{ flex: '1 1 28%' }}>SKU (optional)</div>
                <div style={{ flex: '0 0 36px' }} />
            </div>

            {variants.length === 0 && (
                <div
                    style={{
                        padding: '10px 14px',
                        background: '#F9F9F9',
                        borderRadius: '8px',
                        color: '#828282',
                        fontSize: '13px',
                        marginBottom: '8px',
                    }}
                >
                    No variants yet. Add at least one.
                </div>
            )}

            {variants.map((row, idx) => {
                const rowErrs = validateRow(row, idx, variants);
                const upDisabled = idx === 0;
                const downDisabled = idx === variants.length - 1;
                const labelName = (row.name || '').trim() || `row ${idx + 1}`;
                const imageSrc = resolveImage(row.image);
                const key = rowKey(row, idx);
                const rowUpload = uploadState[key] || {};
                const isUploading = !!rowUpload.uploading;
                const uploadError = rowUpload.error;
                const isHover = hoverPhotoKey === key;
                return (
                    <div key={key} style={rowStyle}>
                        {/* Order (compact stacked chevrons — matches main RewardCenter) */}
                        <div className='vsr-reorder-col' style={{ flex: '0 0 32px' }}>
                            <button
                                type='button'
                                onClick={() => handleMove(idx, -1)}
                                disabled={upDisabled}
                                aria-label={`Move '${labelName}' up`}
                                title={upDisabled ? '' : `Move '${labelName}' up`}
                                className='vsr-reorder-btn'
                            >
                                <ChevronUp />
                            </button>
                            <button
                                type='button'
                                onClick={() => handleMove(idx, 1)}
                                disabled={downDisabled}
                                aria-label={`Move '${labelName}' down`}
                                title={downDisabled ? '' : `Move '${labelName}' down`}
                                className='vsr-reorder-btn'
                            >
                                <ChevronDown />
                            </button>
                        </div>

                        {/* Photo: single click-to-edit thumbnail. Hover shows × to remove. */}
                        <div
                            style={{
                                flex: '0 0 50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                            }}
                            onMouseEnter={() => setHoverPhotoKey(key)}
                            onMouseLeave={() => setHoverPhotoKey((k) => (k === key ? null : k))}
                        >
                            <input
                                ref={(el) => { fileInputs.current[key] = el; }}
                                type='file'
                                accept='image/*'
                                aria-label={`Upload photo for ${labelName}`}
                                style={{ display: 'none' }}
                                onChange={(e) => handleFilePicked(idx, key, e)}
                            />
                            {isUploading ? (
                                <div
                                    aria-label='Uploading photo'
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '8px',
                                        border: '1px solid #E4E4E4',
                                        background: '#F9F9F9',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Spinner size='sm' />
                                </div>
                            ) : imageSrc ? (
                                <button
                                    type='button'
                                    onClick={() => handlePickPhoto(key)}
                                    aria-label={`Replace photo for ${labelName}`}
                                    title='Click to replace photo'
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        padding: 0,
                                        border: '1px solid #E4E4E4',
                                        borderRadius: '8px',
                                        background: '#F9F9F9',
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        display: 'inline-flex',
                                    }}
                                >
                                    <img
                                        src={imageSrc}
                                        alt=''
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                        }}
                                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                                    />
                                    {/* Hover overlay hints "click to replace" */}
                                    <span
                                        aria-hidden='true'
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'rgba(0,0,0,0.45)',
                                            color: '#fff',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            letterSpacing: '0.3px',
                                            opacity: isHover ? 1 : 0,
                                            transition: 'opacity 0.15s ease',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        Edit
                                    </span>
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    onClick={() => handlePickPhoto(key)}
                                    aria-label={`Choose photo for ${labelName}`}
                                    title='Click to add photo'
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '8px',
                                        border: '1px dashed #C8C8C8',
                                        background: '#F9F9F9',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#9CA3AF',
                                        padding: 0,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                        <rect x='3' y='3' width='18' height='18' rx='2' ry='2' />
                                        <circle cx='8.5' cy='8.5' r='1.5' />
                                        <polyline points='21 15 16 10 5 21' />
                                    </svg>
                                </button>
                            )}
                            {imageSrc && !isUploading && isHover && (
                                <button
                                    type='button'
                                    onClick={(e) => { e.stopPropagation(); handleRemovePhoto(idx); }}
                                    aria-label={`Remove photo for ${labelName}`}
                                    title='Remove photo'
                                    style={{
                                        position: 'absolute',
                                        top: '-6px',
                                        right: '-6px',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        border: '1px solid #E0E0E0',
                                        background: '#fff',
                                        color: '#1C1C1C',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        cursor: 'pointer',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                                    }}
                                >
                                    <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                        <line x1='18' y1='6' x2='6' y2='18' />
                                        <line x1='6' y1='6' x2='18' y2='18' />
                                    </svg>
                                </button>
                            )}
                        </div>

                        <div style={{ flex: '1 1 36%' }}>
                            <input
                                type='text'
                                placeholder='e.g. Red'
                                value={row.name || ''}
                                onChange={(e) => handleField(idx, 'name', e.target.value)}
                                maxLength={NAME_MAX}
                                aria-label='Variant name'
                                style={rowErrs.name ? inputErrorStyle : inputStyle}
                                title={rowErrs.name || ''}
                            />
                        </div>
                        <div style={{ flex: '0 0 22%' }}>
                            <input
                                type='number'
                                min={0}
                                step={1}
                                placeholder='0'
                                value={row.stock ?? ''}
                                aria-label='Variant stock'
                                onChange={(e) => {
                                    const v = e.target.value;
                                    // Keep as string so user can clear; coerce on submit.
                                    handleField(idx, 'stock', v === '' ? '' : Number(v));
                                }}
                                style={rowErrs.stock ? inputErrorStyle : inputStyle}
                                title={rowErrs.stock || ''}
                            />
                        </div>
                        <div style={{ flex: '1 1 28%' }}>
                            <input
                                type='text'
                                placeholder='Optional'
                                value={row.sku || ''}
                                aria-label='Variant SKU'
                                onChange={(e) => handleField(idx, 'sku', e.target.value)}
                                maxLength={SKU_MAX}
                                style={rowErrs.sku ? inputErrorStyle : inputStyle}
                                title={rowErrs.sku || ''}
                            />
                        </div>
                        <div style={{ flex: '0 0 36px', display: 'flex', justifyContent: 'center' }}>
                            <button
                                type='button'
                                onClick={() => handleRemove(idx)}
                                aria-label={`Remove '${labelName}'`}
                                title={`Remove '${labelName}'`}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '6px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Del />
                            </button>
                        </div>
                    </div>
                );
            })}

            {/* Per-row upload errors (shown under the row that errored) */}
            {variants.map((row, idx) => {
                const key = rowKey(row, idx);
                const e = (uploadState[key] && uploadState[key].error) || null;
                if (!e) return null;
                return (
                    <div
                        key={`err-${key}`}
                        style={{
                            marginTop: '-4px',
                            marginBottom: '8px',
                            paddingLeft: '94px',
                            color: '#E63946',
                            fontFamily: 'Outfit',
                            fontSize: '12px',
                        }}
                    >
                        {e}
                    </div>
                );
            })}

            <button
                type='button'
                onClick={handleAdd}
                style={{
                    background: 'transparent',
                    border: '1px dashed #362FD8',
                    color: '#362FD8',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontFamily: 'Outfit',
                    fontWeight: 500,
                    fontSize: '14px',
                    marginTop: '4px',
                }}
            >
                + Add variant
            </button>

            {error && (
                <div
                    style={{
                        marginTop: '10px',
                        color: '#E63946',
                        fontSize: '13px',
                        fontFamily: 'Outfit',
                    }}
                >
                    {error}
                </div>
            )}
        </div>
    );
};

// Exported helper so modals can run the same validation before submit.
// Returns { valid: bool, message?: string }.
export const validateVariants = (variants) => {
    if (!Array.isArray(variants) || variants.length === 0) {
        return { valid: false, message: 'Add at least one variant.' };
    }
    const seen = new Set();
    for (let i = 0; i < variants.length; i += 1) {
        const row = variants[i];
        const name = (row.name || '').trim();
        if (!name) return { valid: false, message: `Row ${i + 1}: variant name is required.` };
        if (name.length > NAME_MAX) {
            return { valid: false, message: `Row ${i + 1}: variant name is too long.` };
        }
        const lower = name.toLowerCase();
        if (seen.has(lower)) {
            return { valid: false, message: `Duplicate variant name "${name}".` };
        }
        seen.add(lower);

        const stockNum = Number(row.stock);
        if (
            row.stock === '' ||
            row.stock === null ||
            row.stock === undefined ||
            Number.isNaN(stockNum) ||
            !Number.isInteger(stockNum) ||
            stockNum < 0
        ) {
            return { valid: false, message: `Row ${i + 1}: stock must be a whole number ≥ 0.` };
        }

        const sku = (row.sku || '').trim();
        if (sku && sku.length > SKU_MAX) {
            return { valid: false, message: `Row ${i + 1}: SKU is too long.` };
        }

        const image = (row.image || '').trim();
        if (image && image.length > IMAGE_MAX) {
            return { valid: false, message: `Row ${i + 1}: photo URL is too long.` };
        }
    }
    return { valid: true };
};

// Build the JSON payload the server expects: trimmed names/skus/images, integer stock,
// preserves _id when present so edits target the same variant.
export const variantsForSubmit = (variants) =>
    variants.map((row) => {
        const out = {
            name: (row.name || '').trim(),
            stock: Number(row.stock) || 0,
        };
        const sku = (row.sku || '').trim();
        if (sku) out.sku = sku;
        const image = (row.image || '').trim();
        if (image) out.image = image;
        if (row._id) out._id = row._id;
        return out;
    });

export default VariantEditor;
